// filepath: apps/web/src/definitions/queryKeys.ts
//
// TanStack Query cache-key factory for definitions.
//
// INV-1 mitigation (Risk R-1, 02-impact-analysis.md §10.1): every cache key
// includes `tenantSlug` so a definition loaded for tenant A can never be
// served from cache when the browser navigates to tenant B. ARCHITECTURE_WEB.md
// §4 specifies `['definition', 'form', id, version]`; the tenantSlug is
// prepended here as a renderer-side mitigation that does not require
// backend changes.
//
// Versions:
//   - Pinned: `version: <integer>` (FR-BPM-10, used by in-flight task interaction)
//   - Active: `version: 'active'` (admin/builder views; never used at runtime
//     for in-flight processes)
//
// FR-UI-CORE-3: the cache-key factory is the canonical site that emits
// the `'active'` literal. The static-guard test
// `apps/web/src/definitions/staticGuards.test.ts` AC-4b exempts ONLY this
// file and the `key.version === 'active'` branch in `useDefinition.ts`.
// Any new useDefinition consumer that hardcodes `version: 'active'` is a
// silent-upgrade regression against FR-BPM-10.

import type { DefinitionKey } from "./types";

/**
 * Canonical TanStack Query cache-key factory for definitions.
 *
 * Shape: `['definition', tenantSlug, '<type>', id, version]`
 *
 * The `tenantSlug` segment is mandatory (INV-1 mitigation, R-1). Every
 * per-type factory below takes `tenantSlug` as its first positional
 * argument and places it as the SECOND segment of the cache key tuple.
 *
 * Type coverage (FR-UI-CORE-3):
 *   - forms    / form     — form definitions (FR-FORM-3 baseline)
 *   - processes / process — process definitions (consumer: FR-UI-PROC-1)
 *   - entities / entity   — entity definitions (consumer: FR-UI-ENT-1)
 */
export const definitionKeys = {
  all: ["definition"] as const,

  // Per-type list roots (used by `invalidateQueries(definitionKeys.forms(t))` etc.).
  forms: (tenantSlug: string) => ["definition", tenantSlug, "form"] as const,
  processes: (tenantSlug: string) =>
    ["definition", tenantSlug, "process"] as const,
  entities: (tenantSlug: string) =>
    ["definition", tenantSlug, "entity"] as const,

  // Per-type single-resource keys.
  form: (tenantSlug: string, id: string, version: number | "active") =>
    ["definition", tenantSlug, "form", id, version] as const,
  process: (tenantSlug: string, id: string, version: number | "active") =>
    ["definition", tenantSlug, "process", id, version] as const,
  entity: (tenantSlug: string, id: string, version: number | "active") =>
    ["definition", tenantSlug, "entity", id, version] as const,
};

/**
 * Build the cache key for a definition lookup. Used internally by
 * useDefinition — callers should not call this directly.
 *
 * Dispatches on `key.type`:
 *   - 'form'    → definitionKeys.form(tenantSlug, id, version)
 *   - 'process' → definitionKeys.process(tenantSlug, id, version)
 *   - 'entity'  → definitionKeys.entity(tenantSlug, id, version)
 *
 * The `tenantSlug` is the second segment in every emitted key — the
 * INV-1 / R-1 mitigation that prevents cross-tenant cache collisions.
 */
export function definitionQueryKey(
  tenantSlug: string,
  key: DefinitionKey,
): readonly unknown[] {
  switch (key.type) {
    case "form":
      return definitionKeys.form(tenantSlug, key.id, key.version);
    case "process":
      return definitionKeys.process(tenantSlug, key.id, key.version);
    case "entity":
      return definitionKeys.entity(tenantSlug, key.id, key.version);
  }
}
